<template>
  <div>
    <HeroHeader
      eyebrow="Configuración"
      title="Administradores"
      :counter-num="loaded ? list.length : null"
      counter-unit="admins"
    >
      <template #toolbar>
        <button @click="showAddSidebar = true" class="btn">+ Agregar</button>
      </template>
    </HeroHeader>

    <div class="filter-bar filter-bar--no-top" v-if="list.length > 1">
      <SortSelect :options="sortOptions" v-model="sortBy" />
    </div>

    <div v-if="list.length > 0">
      <div v-for="a in sortedList" :key="a.uid" class="tenant-row">
        <div class="tenant-row__avatar tenant-row__avatar--neutral">
          {{ initials(a) }}
        </div>
        <div class="tenant-row__body">
          <div class="tenant-row__name">{{ a.name ? a.name + ' ' + a.lastName : a.email }}</div>
          <div class="tenant-row__meta">{{ a.email }}{{ a.document ? ' · ' + a.document : '' }}</div>
        </div>
        <div class="tenant-row__right">
          <span class="tenant-row__status tenant-row__status--neutral">Admin</span>
          <span class="tenant-row__detail">{{ a.addedAt ? formatDate(a.addedAt) : '—' }}</span>
        </div>
        <div class="tenant-row__actions">
          <span class="dt-icon dt-icon--danger" title="Quitar acceso admin" @click="confirmRevoke(a)">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/>
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
            </svg>
          </span>
        </div>
      </div>
    </div>

    <EmptyState v-else title="Sin administradores" message="No hay administradores registrados."/>

    <AddAdmin
      :show-navbar="showAddSidebar"
      title="Agregar administrador"
      @added="onAdded"
      @close="showAddSidebar = false"
      :class="{'show': showAddSidebar}"
    />
    <Shadow :class="{'show': showAddSidebar}" />

    <ConfirmDialog
      :show="confirmDialog.show"
      title="Quitar acceso admin"
      :message="`¿Quitar acceso admin a ${confirmDialog.target ? confirmDialog.target.email : ''}?`"
      confirm-text="Quitar"
      cancel-text="Cancelar"
      variant="danger"
      :loading="confirmDialog.loading"
      @confirm="revokeAdmin"
      @cancel="confirmDialog.show = false"
    />
  </div>
</template>

<script>
import { db, functions } from '@/firebase/firebaseConfig'
import AddAdmin from './AddAdmin'
import Shadow from '@/components/Shadow'
import ConfirmDialog from '@/components/ConfirmDialog'
import HeroHeader from '@/components/HeroHeader'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import { sortItems } from '@/utils/sort'

export default {
  name: 'AdminsApp',
  components: { AddAdmin, Shadow, ConfirmDialog, HeroHeader, EmptyState, SortSelect },
  data () {
    return {
      list: [],
      loaded: false,
      sortBy: 'recent',
      sortOptions: [
        { value: 'recent', label: 'Más recientes', field: 'addedAt', type: 'date', dir: 'desc' },
        { value: 'oldest', label: 'Más antiguos', field: 'addedAt', type: 'date', dir: 'asc' },
        { value: 'name', label: 'Nombre (A–Z)', field: 'name', type: 'string', dir: 'asc' }
      ],
      showAddSidebar: false,
      confirmDialog: { show: false, loading: false, target: null }
    }
  },
  computed: {
    sortedList () {
      return sortItems(this.list, this.sortOptions.find(o => o.value === this.sortBy))
    }
  },
  async created () { await this.loadAdmins() },
  methods: {
    initials (a) {
      const name = a.name ? a.name + ' ' + a.lastName : a.email
      return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    },
    formatDate (date) {
      if (!date) return '—'
      return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    async loadAdmins () {
      try {
        this.$store.dispatch('showProgress')
        this.list = []
        const snap = await db.collection('admins').orderBy('addedAt', 'asc').get()
        snap.forEach(doc => {
          const d = doc.data()
          this.list.push({
            uid: doc.id,
            email: d.email,
            name: d.name || null,
            lastName: d.lastName || null,
            document: d.document || null,
            addedAt: d.addedAt ? d.addedAt.toDate() : null
          })
        })
        this.loaded = true
      } catch (e) {
        this.$toast.error('Error al cargar administradores')
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },
    onAdded (admin) {
      this.list.push(admin)
      this.showAddSidebar = false
    },
    confirmRevoke (admin) {
      this.confirmDialog.target = admin
      this.confirmDialog.show = true
    },
    async revokeAdmin () {
      try {
        this.confirmDialog.loading = true
        const callable = functions.httpsCallable('admins-revokeAdmin')
        await callable({ uid: this.confirmDialog.target.uid })
        this.list = this.list.filter(a => a.uid !== this.confirmDialog.target.uid)
        this.$toast.success('Acceso admin removido')
        this.confirmDialog.show = false
      } catch (e) {
        if (e.code === 'functions/failed-precondition') {
          this.$toast.error('No puedes quitarte el acceso a ti mismo')
        } else {
          this.$toast.error('Error al revocar el acceso')
        }
      } finally {
        this.confirmDialog.loading = false
      }
    }
  }
}
</script>
